import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import UserListFilter from '../../../features/userManagement/userFilter';
import UserListTable from '../../../features/userManagement/userListTable';
import { errorToast, successToast } from '../../../helpers';
import MainContainer from '../../../layout/MainContainer';
import {
  useAddSubadminMutation,
  useLazyAllsubAdminQuery,
  useLazyGetAllRolesQuery,
  useUpdateSubadminMutation,
} from '../../../service/user&Roles';
import { CommonBody } from '../../../types/General';
import { generateEncryptedKeyBody } from '../../../utils/crypto';
import { UploadMedia } from '../../../utils/mediaUpload';

const UserList = () => {
  const navigate = useNavigate();
  const [flag, setFlag] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [openWarnModal, setOpenWarnModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [error, setError] = useState(false);
  const [profileImage, setProfileImage] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [profile_picture_id, setProfile_picture_id] = useState<number | null>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('+91');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<any>([]);
  const [page, setPage] = useState<number>(1);
  const [getSubadmins] = useLazyAllsubAdminQuery();
  const [addSubadmin] = useAddSubadminMutation();
  const [updateSubadmin] = useUpdateSubadminMutation();
  const [getRoles] = useLazyGetAllRolesQuery();

  const handleChangePhone = (phone: any, country: any) => {
    setPhone(phone?.replace(country.dialCode, ''));
    setDialCode(country?.dialCode);
  };
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  // fatch subAdmin
  const fetchSubadmins = async () => {
    setLoading(true);
    try {
      const response = await getSubadmins({
        page: page,
        limit: 10,
        // search: debouncedSearchTerm.trim(),
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        setRows(response?.data?.user);
        setCount(response?.data?.count);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setError(false);
    setSelectedId('');
    setProfileImage('');
    setFirstName('');
    setPhone('');
    setDialCode('+971');
    setEmail('');
    setProfile_picture_id(null);
    setSelectedRole([]);
    setOpenModal(false);
  };

  //image Upload
  const handleProfileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage('');
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setProfileImage(imageUrl?.data?.image);
      setProfile_picture_id(imageUrl?.data?.[0]?.id);
    }
  };

  //fatch Roles
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await getRoles({}).unwrap();
      setLoading(false);
      if (response?.statusCode === 200) {
        setRoles(response?.data?.SubAdminAccess || []);
        setSelectedRole(response?.data?.SubAdminAccess || []);
      } else {
        setRoles([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || '');
      setLoading(false);
    }
  };

  //add subadmin
  const handleSubmit = async () => {
    setError(true);
    if (!firstName?.length || firstName?.length <= 2) {
      return;
    }
    if (!email?.length) {
      return;
    }
    if (phone?.length <= 5) {
      return;
    }
    if (!selectedRole) {
      errorToast('Please select a role');
      return;
    }
    const body = {
      name: firstName,
      phoneNo: phone,
      dialCode: dialCode,
      email: email,
      accessRoleId: selectedRole,
      image: profileImage,
    };
    try {
      setLoading(true);
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      let response;
      if (selectedId) {
        response = await updateSubadmin({
          body: encryptedBody,
          id: selectedId,
        }).unwrap();
      } else {
        response = await addSubadmin(encryptedBody).unwrap();
      }
      if (response?.statusCode === 200) {
        if (selectedId) {
          successToast('Subadmin details updated successfully');
        } else {
          successToast('Subadmin details added successfully');
        }
        setOpenModal(false);
        handleCloseModal();
      }
    } catch (error: any) {
      errorToast(error?.data?.message || '');
      console.error(error);
    } finally {
      setLoading(false);
      setFlag(!flag);

      fetchSubadmins();
    }
  };

  useEffect(() => {
    fetchSubadmins();
    fetchRoles();
  }, [debouncedSearchTerm, page]);
  return (
    <>
      <MainContainer>
        <div className="main_title">
          <h1>Subadmin List</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> - Subadmin Management -
            Subadmin List
          </p>
        </div>

        <div className="cards">
          <UserListFilter
            handleProfileUpload={handleProfileUpload}
            setSelectedImage={setProfileImage}
            roles={roles}
            setRoles={setRoles}
            handleSubmit={handleSubmit}
            profileImage={profileImage}
            setProfileImage={setProfileImage}
            name={firstName}
            setName={setFirstName}
            email={email}
            setEmail={setEmail}
            phone={phone}
            handleChangePhone={handleChangePhone}
            dialCode={dialCode}
            setDialCode={setDialCode}
            openModal={openModal}
            setOpenModal={setOpenModal}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            onClose={handleCloseModal}
            debouncedSearchTerm={debouncedSearchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            setPage={setPage}
            page={page}
            loading={loading}
            setLoading={setLoading}
          />
          <UserListTable
            flag={flag}
            handleProfileUpload={handleProfileUpload}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            debouncedSearchTerm={debouncedSearchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            page={page}
            selectedImage={profileImage}
            setSelectedImage={setProfileImage}
            roles={roles}
            setRoles={setRoles}
            handleSubmit={handleSubmit}
            profileImage={profileImage}
            setProfileImage={setProfileImage}
            name={firstName}
            setName={setFirstName}
            email={email}
            setEmail={setEmail}
            phone={phone}
            setPhone={setPhone}
            handleChangePhone={handleChangePhone}
            dialCode={dialCode}
            setDialCode={setDialCode}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            count={count}
          />
        </div>
      </MainContainer>
    </>
  );
};

export default UserList;
